import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  ContentfulEvent,
  Zone,
  getEventListing,
  getExperiences,
  getRestaurants,
  getZones,
} from '@wbk/contentful/api';
import useExploreQuery from './use-explore-query';

type Props = {
  predefined?: Parameters<typeof useExploreQuery>[0]['predefined'];
  enabled?: boolean;
};

const useExploreFetch = ({ predefined, enabled = true }: Props) => {
  const { query, perPage, search } = useExploreQuery({ predefined });
  const canFetch = !!query && enabled;

  const {
    fetchNextPage: fetchMoreEvents,
    hasNextPage: hasMoreEvents,
    isFetching: eventsLoading,
    data: events,
  } = useInfiniteQuery({
    queryKey: ['getEvents', JSON.stringify(query)],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      getEventListing({
        ...query,
        skip: (pageParam - 1) * perPage,
      }),
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const totalRendered = lastPageParam * perPage;
      const total = lastPage.total;
      return total > totalRendered ? lastPageParam + 1 : undefined;
    },
    enabled: canFetch,
  });

  const {
    fetchNextPage: fetchMoreExperiences,
    hasNextPage: hasMoreExperiences,
    isFetching: experiencesLoading,
    data: experiences,
  } = useInfiniteQuery({
    queryKey: ['getExperiences', JSON.stringify(query)],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => getExperiences({ ...query, skip: (pageParam - 1) * perPage }),
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const totalRendered = lastPageParam * perPage;
      const total = lastPage.total;
      return total > totalRendered ? lastPageParam + 1 : undefined;
    },
    enabled: canFetch,
  });

  const {
    fetchNextPage: fetchMoreRestaurants,
    hasNextPage: hasMoreRestaurants,
    isFetching: restaurantsLoading,
    data: restaurants,
  } = useInfiniteQuery({
    queryKey: ['getRestaurants', JSON.stringify(query)],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => getRestaurants({ ...query, skip: (pageParam - 1) * perPage }),
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const totalRendered = lastPageParam * perPage;
      const total = lastPage.total;
      return total > totalRendered ? lastPageParam + 1 : undefined;
    },
    enabled: canFetch,
  });

  const {
    fetchNextPage: fetchMoreZones,
    hasNextPage: hasMoreZones,
    isFetching: zonesLoading,
    data: zones,
  } = useInfiniteQuery({
    queryKey: ['getZones', JSON.stringify(query)],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => getZones({ ...query, skip: (pageParam - 1) * perPage }),
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const totalRendered = lastPageParam * perPage;
      const total = lastPage.total;
      return total > totalRendered ? lastPageParam + 1 : undefined;
    },
    select: (data) => ({
      pageParams: data.pageParams,
      pages: data.pages.map((page) => ({
        ...page,
        settledAt: 0,
      })),
    }),
    // Fetch zones only on search
    enabled: canFetch && !!search,
  });

  const data = useMemo(() => {
    if (!canFetch) {
      return { total: 0, items: [] };
    }

    const allZones = zones?.pages || [];
    const allEvents = events?.pages || [];
    const allExperiences = experiences?.pages || [];
    const allRestaurants = restaurants?.pages || [];

    // A hack for the sorting
    const queries = [allZones, allEvents, allExperiences, allRestaurants];
    const all = queries.flat();
    const sortedBySettledAt = all
      .sort((a, b) => a.settledAt - b.settledAt)
      .flatMap((p) => p.items as (ContentfulEvent | Zone)[]);

    const total = queries.reduce((acc, q) => acc + (q[0]?.total || 0), 0);

    return {
      total,
      items: sortedBySettledAt,
    };
  }, [canFetch, events?.pages, experiences?.pages, restaurants?.pages, zones?.pages]);

  const loading = eventsLoading || experiencesLoading || restaurantsLoading || zonesLoading;

  return {
    zones,
    allEvents: data.items,
    total: data.total,
    hasMoreEvents,
    hasMoreExperiences,
    hasMoreRestaurants,
    hasMoreZones,
    fetchMoreEvents,
    fetchMoreExperiences,
    fetchMoreRestaurants,
    fetchMoreZones,
    loading,
  };
};

export default useExploreFetch;
